import { useMemo } from 'react';
import { Configuration, StoresApi } from '@/api';
import { Store, StoreAssociation } from '@/models/Store';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQueries, useQueryClient } from '@tanstack/react-query';

const associations = [StoreAssociation.RETAILER, StoreAssociation.ORGANIZATION];

const storeQueryKey = (storeId: string, supplierId?: string) => [
  'store',
  storeId,
  supplierId,
  associations,
];

const useGetStoresByIdsQuery = (storeIds: string[], supplierId?: string) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const storesApi = useMemo(() => new StoresApi(new Configuration(auth)), [auth]);

  const queries = useQueries({
    queries: storeIds.map((storeId) => ({
      queryKey: storeQueryKey(storeId, supplierId),
      queryFn: () =>
        supplierId
          ? storesApi.getStoreWithSupplierContext(storeId, supplierId, associations)
          : storesApi.getStore(storeId, associations),
    })),
  });

  const stores = queries
    .map((query) => query.data)
    .filter((store): store is Store => store !== undefined);
  const error = queries.find((query) => query.error)?.error ?? null;
  const invalidateQuery = () =>
    Promise.all(
      storeIds.map((storeId) =>
        queryClient.invalidateQueries({ queryKey: storeQueryKey(storeId, supplierId) }),
      ),
    );

  return {
    stores,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    error,
    invalidateQuery,
  };
};

export default useGetStoresByIdsQuery;
